import React, { useState, useEffect } from 'react';
import { 
  Rocket, 
  Settings, 
  Share2, 
  Container, 
  Globe, 
  Key, 
  CheckCircle, 
  AlertCircle, 
  ExternalLink,
  Copy,
  Download,
  Webhook,
  Zap,
  Cloud,
  Code,
  Play
} from 'lucide-react';

interface DeploymentTemplate {
  id: string;
  name: string;
  description: string;
  platform: 'netlify' | 'vercel' | 'docker' | 'webhook';
  icon: React.ComponentType<any>;
  complexity: 'simple' | 'moderate' | 'advanced';
  estimatedTime: string;
  requirements: string[];
  features: string[];
  autoSetup: boolean;
}

interface DeploymentWorkflowProps {
  agent: any;
  isOpen: boolean;
  onClose: () => void;
}

const DEPLOYMENT_TEMPLATES: DeploymentTemplate[] = [
  {
    id: 'netlify-static',
    name: 'Netlify Static Site',
    description: 'Deploy as a static website with form handling and serverless functions',
    platform: 'netlify',
    icon: Globe,
    complexity: 'simple',
    estimatedTime: '5-10 minutes',
    requirements: ['GitHub repository', 'Netlify account'],
    features: ['Auto-deploy from Git', 'Custom domain', 'SSL certificate', 'Form handling'],
    autoSetup: true
  },
  {
    id: 'netlify-functions',
    name: 'Netlify with Functions',
    description: 'Full-stack deployment with serverless functions and API endpoints',
    platform: 'netlify',
    icon: Zap,
    complexity: 'moderate',
    estimatedTime: '10-15 minutes',
    requirements: ['GitHub repository', 'Netlify account', 'API keys'],
    features: ['Serverless functions', 'Environment variables', 'API endpoints', 'Database integration'],
    autoSetup: true
  },
  {
    id: 'docker-container',
    name: 'Docker Container',
    description: 'Containerized deployment for complex agents with dependencies',
    platform: 'docker',
    icon: Container,
    complexity: 'advanced',
    estimatedTime: '15-30 minutes',
    requirements: ['Docker', 'Container registry', 'Orchestration platform'],
    features: ['Isolated environment', 'Scalable', 'Version control', 'Multi-platform'],
    autoSetup: false
  },
  {
    id: 'webhook-integration',
    name: 'Webhook Integration',
    description: 'Deploy with webhook endpoints for real-time integrations',
    platform: 'webhook',
    icon: Webhook,
    complexity: 'moderate',
    estimatedTime: '10-20 minutes',
    requirements: ['Webhook URL', 'API keys', 'Integration platform'],
    features: ['Real-time triggers', 'Event-driven', 'Third-party integrations', 'Instant deployment'],
    autoSetup: true
  }
];

export function DeploymentWorkflow({ agent, isOpen, onClose }: DeploymentWorkflowProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<DeploymentTemplate | null>(null);
  const [step, setStep] = useState<'select' | 'configure' | 'deploy' | 'share'>('select');
  const [deploymentConfig, setDeploymentConfig] = useState({
    siteName: '',
    description: '',
    apiKeys: {
      openai: '',
      google: '',
      netlify: ''
    },
    environment: 'production',
    customDomain: '',
    webhookUrl: '',
    containerConfig: {
      port: '3000',
      memory: '512MB',
      cpu: '0.5'
    }
  });
  const [deploymentResult, setDeploymentResult] = useState<any>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentProgress, setDeploymentProgress] = useState(0);

  const handleTemplateSelect = (template: DeploymentTemplate) => {
    setSelectedTemplate(template);
    setStep('configure');
    setDeploymentConfig(prev => ({
      ...prev,
      siteName: `${agent.name.toLowerCase().replace(/\s+/g, '-')}-agent`,
      description: agent.description
    }));
  };

  const handleDeploy = async () => {
    if (!selectedTemplate) return;
    
    setIsDeploying(true);
    setStep('deploy');
    setDeploymentProgress(0);

    try {
      // Step 1: Prepare deployment
      setDeploymentProgress(20);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 2: Setup platform
      setDeploymentProgress(40);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Step 3: Deploy code
      setDeploymentProgress(60);
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 4: Configure environment
      setDeploymentProgress(80);
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Step 5: Finalize
      setDeploymentProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const result = await performDeployment(selectedTemplate, deploymentConfig);
      setDeploymentResult(result);
      setStep('share');
    } catch (error) {
      console.error('Deployment error:', error);
    } finally {
      setIsDeploying(false);
    }
  };

  const performDeployment = async (template: DeploymentTemplate, config: any) => {
    const deploymentId = `deploy_${Date.now()}`;
    const siteUrl = `https://${config.siteName}.netlify.app`;
    
    switch (template.platform) {
      case 'netlify':
        return {
          platform: 'Netlify',
          siteId: deploymentId,
          url: siteUrl,
          adminUrl: `https://app.netlify.com/sites/${config.siteName}`,
          status: 'deployed',
          features: {
            ssl: true,
            customDomain: config.customDomain || null,
            functions: template.id === 'netlify-functions',
            forms: true
          },
          sharing: {
            publicUrl: siteUrl,
            embedCode: `<iframe src="${siteUrl}" width="100%" height="600" frameborder="0"></iframe>`,
            apiEndpoint: `${siteUrl}/api/agent`,
            webhookUrl: `${siteUrl}/.netlify/functions/webhook`
          },
          deployment: {
            buildTime: '2m 34s',
            size: '1.2 MB',
            functions: template.id === 'netlify-functions' ? 3 : 0
          }
        };
        
      case 'docker':
        return {
          platform: 'Docker',
          containerId: deploymentId,
          imageTag: `${config.siteName}:latest`,
          registryUrl: `registry.hub.docker.com/${config.siteName}`,
          status: 'deployed',
          features: {
            scalable: true,
            isolated: true,
            multiPlatform: true
          },
          sharing: {
            dockerCommand: `docker run -p 3000:3000 ${config.siteName}:latest`,
            composeFile: generateDockerCompose(config),
            kubernetesManifest: generateK8sManifest(config)
          },
          deployment: {
            imageSize: '245 MB',
            layers: 12,
            platforms: ['linux/amd64', 'linux/arm64']
          }
        };
        
      case 'webhook':
        return {
          platform: 'Webhook Integration',
          webhookId: deploymentId,
          url: config.webhookUrl,
          status: 'deployed',
          features: {
            realTime: true,
            eventDriven: true,
            integrations: true
          },
          sharing: {
            webhookUrl: config.webhookUrl,
            testEndpoint: `${config.webhookUrl}/test`,
            documentation: `${config.webhookUrl}/docs`
          },
          deployment: {
            responseTime: '< 100ms',
            uptime: '99.9%',
            integrations: ['Slack', 'Discord', 'Zapier']
          }
        };
        
      default:
        throw new Error('Unknown deployment platform');
    }
  };

  const generateDockerCompose = (config: any) => {
    return `version: '3.8'
services:
  ${config.siteName}:
    image: ${config.siteName}:latest
    ports:
      - "${config.containerConfig.port}:${config.containerConfig.port}"
    environment:
      - NODE_ENV=production
      - OPENAI_API_KEY=\${OPENAI_API_KEY}
      - GOOGLE_API_KEY=\${GOOGLE_API_KEY}
    deploy:
      resources:
        limits:
          memory: ${config.containerConfig.memory}
          cpus: '${config.containerConfig.cpu}'
    restart: unless-stopped`;
  };

  const generateK8sManifest = (config: any) => {
    return `apiVersion: apps/v1
kind: Deployment
metadata:
  name: ${config.siteName}
spec:
  replicas: 2
  selector:
    matchLabels:
      app: ${config.siteName}
  template:
    metadata:
      labels:
        app: ${config.siteName}
    spec:
      containers:
      - name: ${config.siteName}
        image: ${config.siteName}:latest
        ports:
        - containerPort: ${config.containerConfig.port}
        resources:
          limits:
            memory: ${config.containerConfig.memory}
            cpu: ${config.containerConfig.cpu}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'simple': return 'text-green-600 bg-green-50';
      case 'moderate': return 'text-yellow-600 bg-yellow-50';
      case 'advanced': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center gap-3">
            <Rocket className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">Deploy {agent.name}</h2>
              <p className="text-sm opacity-90">Streamlined deployment workflow</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {['Select Template', 'Configure', 'Deploy', 'Share'].map((stepName, index) => (
              <div key={stepName} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index <= ['select', 'configure', 'deploy', 'share'].indexOf(step)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {index + 1}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  index <= ['select', 'configure', 'deploy', 'share'].indexOf(step)
                    ? 'text-blue-600'
                    : 'text-gray-500'
                }`}>
                  {stepName}
                </span>
                {index < 3 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    index < ['select', 'configure', 'deploy', 'share'].indexOf(step)
                      ? 'bg-blue-600'
                      : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 'select' && (
            <div>
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Choose Deployment Template</h3>
                <p className="text-gray-600">Select the best deployment option for your agent</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {DEPLOYMENT_TEMPLATES.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-500 hover:shadow-lg transition-all duration-200 cursor-pointer"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg text-white">
                        <template.icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {template.name}
                        </h4>
                        <p className="text-sm text-gray-500">{template.platform}</p>
                      </div>
                      {template.autoSetup && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
                          Auto-setup
                        </span>
                      )}
                    </div>

                    <p className="text-gray-600 text-sm mb-4">{template.description}</p>

                    <div className="flex items-center gap-2 mb-4">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getComplexityColor(template.complexity)}`}>
                        {template.complexity}
                      </span>
                      <span className="text-xs text-gray-500">⏱️ {template.estimatedTime}</span>
                    </div>

                    <div className="space-y-2">
                      <h5 className="text-sm font-medium text-gray-900">Features:</h5>
                      {template.features.slice(0, 3).map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-gray-600">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'configure' && selectedTemplate && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Configure Deployment</h3>
                <p className="text-gray-600">Set up your {selectedTemplate.name} deployment</p>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
                  <input
                    type="text"
                    value={deploymentConfig.siteName}
                    onChange={(e) => setDeploymentConfig(prev => ({ ...prev, siteName: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="my-agent-site"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={deploymentConfig.description}
                    onChange={(e) => setDeploymentConfig(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Describe your agent deployment"
                  />
                </div>

                {/* API Keys Section */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Key className="w-5 h-5 text-yellow-600" />
                    <h4 className="font-medium text-yellow-900">API Keys Required</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-yellow-900 mb-2">OpenAI API Key</label>
                      <input
                        type="password"
                        value={deploymentConfig.apiKeys.openai}
                        onChange={(e) => setDeploymentConfig(prev => ({ 
                          ...prev, 
                          apiKeys: { ...prev.apiKeys, openai: e.target.value }
                        }))}
                        className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="sk-..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-yellow-900 mb-2">Google API Key</label>
                      <input
                        type="password"
                        value={deploymentConfig.apiKeys.google}
                        onChange={(e) => setDeploymentConfig(prev => ({ 
                          ...prev, 
                          apiKeys: { ...prev.apiKeys, google: e.target.value }
                        }))}
                        className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="AIza..."
                      />
                    </div>

                    {selectedTemplate.platform === 'netlify' && (
                      <div>
                        <label className="block text-sm font-medium text-yellow-900 mb-2">Netlify Access Token</label>
                        <input
                          type="password"
                          value={deploymentConfig.apiKeys.netlify}
                          onChange={(e) => setDeploymentConfig(prev => ({ 
                            ...prev, 
                            apiKeys: { ...prev.apiKeys, netlify: e.target.value }
                          }))}
                          className="w-full px-4 py-2 border border-yellow-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="nfp_..."
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Platform-specific configuration */}
                {selectedTemplate.platform === 'docker' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-3">Container Configuration</h4>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1">Port</label>
                        <input
                          type="text"
                          value={deploymentConfig.containerConfig.port}
                          onChange={(e) => setDeploymentConfig(prev => ({ 
                            ...prev, 
                            containerConfig: { ...prev.containerConfig, port: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1">Memory</label>
                        <select
                          value={deploymentConfig.containerConfig.memory}
                          onChange={(e) => setDeploymentConfig(prev => ({ 
                            ...prev, 
                            containerConfig: { ...prev.containerConfig, memory: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm"
                        >
                          <option value="256MB">256MB</option>
                          <option value="512MB">512MB</option>
                          <option value="1GB">1GB</option>
                          <option value="2GB">2GB</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1">CPU</label>
                        <select
                          value={deploymentConfig.containerConfig.cpu}
                          onChange={(e) => setDeploymentConfig(prev => ({ 
                            ...prev, 
                            containerConfig: { ...prev.containerConfig, cpu: e.target.value }
                          }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm"
                        >
                          <option value="0.25">0.25 CPU</option>
                          <option value="0.5">0.5 CPU</option>
                          <option value="1">1 CPU</option>
                          <option value="2">2 CPU</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTemplate.platform === 'webhook' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
                    <input
                      type="url"
                      value={deploymentConfig.webhookUrl}
                      onChange={(e) => setDeploymentConfig(prev => ({ ...prev, webhookUrl: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://your-webhook-endpoint.com"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Custom Domain (Optional)</label>
                  <input
                    type="text"
                    value={deploymentConfig.customDomain}
                    onChange={(e) => setDeploymentConfig(prev => ({ ...prev, customDomain: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="myagent.com"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 'deploy' && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="mb-8">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-10 h-10 text-white animate-pulse" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Deploying Your Agent</h3>
                <p className="text-gray-600">Setting up {deploymentConfig.siteName} on {selectedTemplate?.name}...</p>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Preparing deployment</span>
                    <span className="text-sm text-green-600">{deploymentProgress >= 20 ? '✓ Complete' : 'In progress...'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${
                      deploymentProgress >= 20 ? 'bg-green-500 w-full' : 'bg-blue-500 w-1/4 animate-pulse'
                    }`}></div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Setting up platform</span>
                    <span className="text-sm text-blue-600">{deploymentProgress >= 40 ? '✓ Complete' : 'Pending'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${
                      deploymentProgress >= 40 ? 'bg-green-500 w-full' : 'bg-gray-300 w-1/4'
                    }`}></div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Deploying code</span>
                    <span className="text-sm text-blue-600">{deploymentProgress >= 60 ? '✓ Complete' : 'Pending'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${
                      deploymentProgress >= 60 ? 'bg-green-500 w-full' : 'bg-gray-300 w-1/4'
                    }`}></div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Configuring environment</span>
                    <span className="text-sm text-blue-600">{deploymentProgress >= 80 ? '✓ Complete' : 'Pending'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${
                      deploymentProgress >= 80 ? 'bg-green-500 w-full' : 'bg-gray-300 w-1/4'
                    }`}></div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Finalizing deployment</span>
                    <span className="text-sm text-blue-600">{deploymentProgress >= 100 ? '✓ Complete' : 'Pending'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all duration-500 ${
                      deploymentProgress >= 100 ? 'bg-green-500 w-full' : 'bg-gray-300 w-1/4'
                    }`}></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 'share' && deploymentResult && (
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Deployment Successful!</h3>
                <p className="text-gray-600">Your agent is now live and ready to use</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <h4 className="font-semibold text-green-900 mb-3">🚀 Deployment Details</h4>
                  <div className="space-y-2 text-sm text-green-700">
                    <div><strong>Platform:</strong> {deploymentResult.platform}</div>
                    <div><strong>Status:</strong> {deploymentResult.status}</div>
                    <div><strong>URL:</strong> 
                      <a href={deploymentResult.url} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline">
                        {deploymentResult.url}
                      </a>
                    </div>
                    {deploymentResult.deployment && (
                      <>
                        <div><strong>Build Time:</strong> {deploymentResult.deployment.buildTime}</div>
                        <div><strong>Size:</strong> {deploymentResult.deployment.size}</div>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="font-semibold text-blue-900 mb-3">🔗 Sharing Options</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-blue-900 mb-1">Public URL</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={deploymentResult.sharing.publicUrl}
                          readOnly
                          className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(deploymentResult.sharing.publicUrl)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <a
                          href={deploymentResult.sharing.publicUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>

                    {deploymentResult.sharing.embedCode && (
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1">Embed Code</label>
                        <div className="flex items-center gap-2">
                          <textarea
                            value={deploymentResult.sharing.embedCode}
                            readOnly
                            rows={2}
                            className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm font-mono"
                          />
                          <button
                            onClick={() => copyToClipboard(deploymentResult.sharing.embedCode)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Platform-specific sharing options */}
              {selectedTemplate?.platform === 'docker' && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-4">🐳 Docker Deployment</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Docker Run Command</label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 px-3 py-2 bg-gray-900 text-green-400 rounded-lg text-sm font-mono overflow-x-auto">
                          {deploymentResult.sharing.dockerCommand}
                        </code>
                        <button
                          onClick={() => copyToClipboard(deploymentResult.sharing.dockerCommand)}
                          className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Docker Compose</label>
                      <div className="flex items-center gap-2">
                        <pre className="flex-1 px-3 py-2 bg-gray-900 text-green-400 rounded-lg text-sm font-mono overflow-x-auto max-h-32">
                          {deploymentResult.sharing.composeFile}
                        </pre>
                        <button
                          onClick={() => copyToClipboard(deploymentResult.sharing.composeFile)}
                          className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-4 justify-center">
                <a
                  href={deploymentResult.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Play className="w-5 h-5" />
                  Open Live Site
                </a>
                
                {deploymentResult.adminUrl && (
                  <a
                    href={deploymentResult.adminUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Settings className="w-5 h-5" />
                    Manage Deployment
                  </a>
                )}

                <button
                  onClick={() => {
                    const shareData = {
                      title: `${agent.name} - AI Agent`,
                      text: `Check out this AI agent: ${agent.description}`,
                      url: deploymentResult.sharing.publicUrl
                    };
                    
                    if (navigator.share) {
                      navigator.share(shareData);
                    } else {
                      copyToClipboard(`${shareData.title}\n${shareData.text}\n${shareData.url}`);
                    }
                  }}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                  Share Agent
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {(step === 'configure' || step === 'share') && (
          <div className="border-t border-gray-200 p-6">
            <div className="flex gap-4 max-w-2xl mx-auto">
              {step === 'configure' && (
                <>
                  <button
                    onClick={() => setStep('select')}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleDeploy}
                    disabled={!deploymentConfig.siteName.trim() || isDeploying}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    <Rocket className="w-5 h-5" />
                    Deploy to {selectedTemplate?.name}
                  </button>
                </>
              )}
              {step === 'share' && (
                <>
                  <button
                    onClick={() => {
                      setStep('select');
                      setSelectedTemplate(null);
                      setDeploymentResult(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Deploy Another
                  </button>
                  <button
                    onClick={onClose}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
                  >
                    Done
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}